import React, { createContext, useContext } from 'react';

import { isNavPathLocked, moduleForNavPath } from '../../licensing/navModules';
import NavLock from './NavLock';

/**
 * True inside a nav container (a sidebar section, sub-group or vendor
 * expander) that already carries the lock for its whole module.
 */
export const ParentLockContext = createContext(false);

/**
 * The lock beside one nav row, only when it says something the heading above
 * does not: the row's own module is not licensed AND its container is not
 * already locked. A locked module shows one lock, on its heading, not one per
 * page (user, 2026-10-06); a row of another module inside a licensed one (Red
 * Team inside Agent Atlas) still shows its own.
 */
export default function RowLock({ to, license }) {
  const parentLocked = useContext(ParentLockContext);
  if (parentLocked || !to || !isNavPathLocked(to, license)) return null;
  return <NavLock module={moduleLabelFor(to, license)} />;
}

/** The licence's name for the module a nav path belongs to ('' if none). */
export function moduleLabelFor(to, license) {
  const mod = moduleForNavPath(to);
  const first = Array.isArray(mod) ? mod[0] : mod;
  return first && license && license.moduleLabel ? license.moduleLabel(first) : '';
}

/**
 * Wrap a container's rows so they leave the lock to it. A container inside a
 * locked one stays locked: the value only ever widens.
 */
export const LockedParent = ({ locked, children }) => {
  const parentLocked = useContext(ParentLockContext);
  return (
    <ParentLockContext.Provider value={parentLocked || Boolean(locked)}>{children}</ParentLockContext.Provider>
  );
};

/** A container's own lock (sub-group, expander), unless a container above already shows it. */
export const ContainerLock = ({ locked, module = '' }) => {
  const parentLocked = useContext(ParentLockContext);
  return locked && !parentLocked ? <NavLock module={module} /> : null;
};
